import React, { PropsWithChildren, useEffect, useState } from 'react';
import { Box, BoxProps } from '@mui/material';
import { styled } from '@mui/styles';
import { ISocialMediaPost } from '../../../dataObjects/models/digitalMedia/SocialMediaPost';
import { enumDigitalMediaDisplayEnvironment } from '../../../dataObjects/enums';
import { MdbError } from '../../../errorObjects/MdbError';
import { DigitalMediaNotAvailableDisplay } from '../DigitalMediaNotAvailableDisplay/DigitalMediaNotAvailableDisplay';
import { MessagesStringAssets } from '../../../assets/stringAssets';
import { EmbedFacebookPost, EmbedInstagram, EmbedLinkedIn, EmbedPinterest, EmbedTikTok, EmbedTruthSocial, EmbedX, EmbedYouTube } from './socialMediaEmbeds';
import { enumSocialMediaPostType } from '../../enums';
import { determineSocialMediaPostDisplaySpecsFromRawReference, ISocialMediaPostDisplaySpecs } from './socialMediaPostDisplayUtilities';
import {
  FacebookProvider,
  Like,
  Share,
  Login,
  Comments,
  CommentsCount,
  Page,
  EmbeddedPost,
  EmbeddedVideo,
  ShareButton
} from 'react-facebook'


/*** Using the Material UI Emotion Styling library, declare 'styled' instances for each area/object. 
 *** NOTE: These must be declared outside of the React Functional Component to ensure that the styled 
 *** objects will be properly rendered within the DOM. 
 ***/

// a styled Box (equivalent to a <div>) that will serve as a base container in a Card View
const StyledContainerForCardView = styled((props: BoxProps) => (
  <Box
    {...props}
  />
))(({ theme }) => ({
  margin: theme.spacing(0, .5, 2, .5),
  border: `2px solid ${theme.palette.primary.main}`,
  borderRadius: '1px',
  display: 'flex',
  justifyContent: 'center',
  flexGrow: 1,
  position: 'relative',
  // height: '250px',
  // maxHeight: '400px',
  // overflowY: 'auto', // 'scroll', // Enable vertical scrolling
  width: 'auto'
})) as typeof Box; // added casting to 'as typeof Box' as workaround for a Material UI issue (see https://github.com/mui/material-ui/issues/38274)

// a styled Box (equivalent to a <div>) that will serve as a base container in a Form View
const StyledContainerForFormView = styled((props: BoxProps) => (
  <Box
    {...props}
  />
))(({ theme }) => ({
  margin: theme.spacing(2, 0),
  border: `2px solid ${theme.palette.primary.main}`,
  borderRadius: '1px',
  display: 'flex',
  justifyContent: 'center',
  flexGrow: 1,
  position: 'relative',
  // height: '250px',
  minHeight: '250px',
  width: 'auto'
})) as typeof Box; // added casting to 'as typeof Box' as workaround for a Material UI issue (see https://github.com/mui/material-ui/issues/38274)

// a styled Box (equivalent to a <div>) that will serve as a base container in a List View
const StyledContainerForListView = styled((props: BoxProps) => (
  <Box
    {...props}
  />
))(({ theme }) => ({
  margin: theme.spacing(1, 2, 1, 0),
  border: `2px solid ${theme.palette.primary.main}`,
  borderRadius: '1px',
  display: 'flex',
  justifyContent: 'center',
  flexGrow: 1,
  position: 'relative',
  // height: '250px',
  // minHeight: '250px',
  minWidth: '250px',
  width: 'auto',

  [theme.breakpoints.down('xs')]: {
    maxWidth: '300px',
  },
  [theme.breakpoints.up('sm')]: {
    maxWidth: '350px',
  },
  [theme.breakpoints.up('md')]: {
    maxWidth: '500px',
  },
})) as typeof Box; // added casting to 'as typeof Box' as workaround for a Material UI issue (see https://github.com/mui/material-ui/issues/38274)

// a styled Box (equivalent to a <div>), providing a background for a not available message
const SocialMediaNotAvailableBox = styled((props: BoxProps) => (
  <Box
    {...props}
  />
))(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.light}, #BBB, #EEE, ${theme.palette.primary.light}, #BBB, #EEE, ${theme.palette.primary.light})`,
  display: 'flex',
  justifyContent: 'center',
}));

/**
 * @interface ISocialMediaPostDisplayProps declares any input properties required for this component.
 */
export interface ISocialMediaPostDisplayProps extends PropsWithChildren<unknown> {
  socialMediaPost: ISocialMediaPost;
  displayEnvironment: enumDigitalMediaDisplayEnvironment;
}

/**
 * @function SocialMediaPostDisplay is a React functional component for displaying a social media post (via an ISocialMediaPost).
 * @param {} props are the properties passed into the component (currently no input properties).
 */
export const SocialMediaPostDisplay: React.FC<ISocialMediaPostDisplayProps> = (props: ISocialMediaPostDisplayProps) => {
  SocialMediaPostDisplay.displayName = 'Social Media Post Display';

  // whether to display console logs (displayConsoleLogs && console.log statements)
  const displayConsoleLogs: boolean = false;

  displayConsoleLogs && console.log(`SocialMediaPostDisplay. Entered SocialMediaPostDisplay`);

  const { socialMediaPost, displayEnvironment } = props;

  // the type of social media post (Facebook, TwitterX, etc.)
  const [socialMediaPostDisplaySpecs, setSocialMediaPostDisplaySpecs] = useState<ISocialMediaPostDisplaySpecs | undefined>(undefined);

  const socialMediaNotAvailableWidth: string = `100%`;
  const socialMediaNotAvailableHeight: string = `100%`;

  // useEffect to establish container and socialMediaPost display component parameters based on the socialMediaPost and display environment
  useEffect(() => {
    setSocialMediaPostDisplaySpecs(determineSocialMediaPostDisplaySpecsFromRawReference(socialMediaPost.url));
  }, [socialMediaPost.url]);


  /**
   * @function renderSocialMediaPostElement Renders the JSX element of the socialMediaPost object to be displayed on the UI.
   * @param {ISocialMediaPost} socialMediaPost The SocialMediaPost object to be rendered.
   */
  function renderSocialMediaPostElement(): React.JSX.Element {

    // default the socialMediaPost element to displaying no socialMediaPost
    let socialMediaPostElement: React.JSX.Element =
      <>
        <SocialMediaNotAvailableBox
          sx={{ width: socialMediaNotAvailableWidth, height: socialMediaNotAvailableHeight }}
        />
      </>;

    try {

      displayConsoleLogs && console.log(`SocialMediaPostDisplay. renderSocialMediaPostElement().`);

      // if there's an established Url for the object, perform logic
      if (socialMediaPostDisplaySpecs && (socialMediaPostDisplaySpecs.socialMediaPostType !== enumSocialMediaPostType.Undefined)) {
        switch (socialMediaPostDisplaySpecs.socialMediaPostType) {
          case enumSocialMediaPostType.FacebookPost:
            // socialMediaPostElement =
            //   <>
            //     <EmbedFacebookPost url={socialMediaPostDisplaySpecs.url} postWidth={socialMediaPostDisplaySpecs.width} postHeight={socialMediaPostDisplaySpecs.height} />
            //   </>

            const facebookApiKey = process.env.REACT_APP_FACEBOOK_DEVELOPER_APP_ID ?? '';

            socialMediaPostElement =
              <>
                <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                  <FacebookProvider appId={facebookApiKey}>
                    <EmbeddedPost href={socialMediaPostDisplaySpecs.url} showText lazy />
                  </FacebookProvider>
                </div >
              </>
            break;

          // case SocialMediaPostType.Gab:
          //   socialMediaPostElement =
          //     <>
          //       <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          //         <a href={socialMediaUrl} />
          //       </div>
          //     </>
          //   break;

          case enumSocialMediaPostType.Instagram:
            // const transformedUrlForInstagram: string = transformUrlAsNeededForInstagram(socialMediaUrl);
            socialMediaPostElement =
              <>
                <EmbedInstagram url={socialMediaPostDisplaySpecs.url} postWidth={socialMediaPostDisplaySpecs.width} postHeight={socialMediaPostDisplaySpecs.height} />
              </>
            break;

          case enumSocialMediaPostType.LinkedIn:
            socialMediaPostElement =
              <>
                <EmbedLinkedIn url={socialMediaPostDisplaySpecs.url} postWidth={socialMediaPostDisplaySpecs.width} postHeight={socialMediaPostDisplaySpecs.height} />
              </>
            break;

          case enumSocialMediaPostType.Pinterest:
            socialMediaPostElement =
              <>
                <EmbedPinterest url={socialMediaPostDisplaySpecs.url} postWidth={socialMediaPostDisplaySpecs.width} postHeight={socialMediaPostDisplaySpecs.height} />
              </>
            break;

          case enumSocialMediaPostType.TikTok:
            socialMediaPostElement =
              <>
                <EmbedTikTok url={socialMediaPostDisplaySpecs.url} postWidth={socialMediaPostDisplaySpecs.width} postHeight={socialMediaPostDisplaySpecs.height} />
              </>
            break;

          case enumSocialMediaPostType.TruthSocial:
            // for a Truth Social post, the default width is what is found in the display specs; however, if we'll be displaying in a FormView, alter the width
            let width = socialMediaPostDisplaySpecs.width;
            if (displayEnvironment == enumDigitalMediaDisplayEnvironment.FormView) {
              width = '530px';
            }

            socialMediaPostElement =
              <>
                <EmbedTruthSocial url={socialMediaPostDisplaySpecs.url} postWidth={width} postHeight={socialMediaPostDisplaySpecs.height} />
              </>
            break;

          case enumSocialMediaPostType.XOrTwitter:
            socialMediaPostElement =
              <>
                <EmbedX url={socialMediaPostDisplaySpecs.url} postWidth={socialMediaPostDisplaySpecs.width} postHeight={socialMediaPostDisplaySpecs.height} />
              </>
            break;

          case enumSocialMediaPostType.YouTube:
            socialMediaPostElement =
              <>
                <EmbedYouTube url={socialMediaPostDisplaySpecs.url} postWidth={socialMediaPostDisplaySpecs.width} postHeight={socialMediaPostDisplaySpecs.height} />
              </>
            break;

          default:
            socialMediaPostElement =
              <>
                <DigitalMediaNotAvailableDisplay displayText={MessagesStringAssets.socialMediaPostSourceOrFormatNotSupported} />
              </>
            break;
        }
      }
    } catch (error) {
      // Do nothing for now. We just want to ensure that the app doesn't crash the page via an unhandled exception if a social media posting doesn't render correctly.
    }

    // socialMediaPostElement =
    //   <>
    //     <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
    //       <iframe src="https://www.facebook.com/plugins/video.php?height=476&href=https%3A%2F%2Fwww.facebook.com%2Freel%2F2285338898983375%2F&show_text=false&width=267&t=0"
    //         // width="267" 
    //         height="476"
    //         style={{ border: 'none', overflow: 'hidden' }}
    //         // style="border:none;overflow:hidden" scrolling="no" frameborder="0" 
    //         allowfullscreen="true"
    //         allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
    //       // allowFullScreen="true"
    //       >
    //       </iframe>
    //     </div>
    //   </>

    // const facebookApiKey = process.env.REACT_APP_FACEBOOK_DEVELOPER_APP_ID ?? '';

    // socialMediaPostElement =
    //   <>
    //     {/* <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}> */}
    //       <FacebookProvider appId={facebookApiKey}>
    //         {/* <EmbeddedPost href="https://www.facebook.com/reel/2285338898983375" showText lazy /> */}
    //         {/* <EmbeddedPost href="https://www.facebook.com/reel/1734849824477680" showText lazy /> */}
    //         <EmbeddedPost href="https://www.facebook.com/reel/1052925284397486" showText lazy />
    //         {/* DOESN'T WORK <EmbeddedPost href="https://www.facebook.com/groups/1143329212669249/permalink/2750393595296128/" showText lazy /> */}
    //       </FacebookProvider>
    //     {/* </div> */}
    //   </>

    return socialMediaPostElement;
  }

  /**
   * @function renderSocialMediaPostContainerAndSocialMediaPostDisplay Renders the JSX element of the socialMediaPost element container and socialMediaPost element to be displayed on the UI.
   */
  function renderSocialMediaPostContainerAndSocialMediaPostDisplay(): React.JSX.Element {

    let socialMediaPostContainerAndSocialMediaPostDisplay: React.JSX.Element =
      <>
      </>

    displayConsoleLogs && console.log(`SocialMediaPostDisplay.renderSocialMediaPostContainerAndSocialMediaPostDisplay(). displayEnvironment: ${displayEnvironment}`);

    switch (displayEnvironment) {
      case enumDigitalMediaDisplayEnvironment.CardView:
        socialMediaPostContainerAndSocialMediaPostDisplay =
          <StyledContainerForCardView >
            {renderSocialMediaPostElement()}
          </StyledContainerForCardView>
        break;

      case enumDigitalMediaDisplayEnvironment.FormView:
        socialMediaPostContainerAndSocialMediaPostDisplay =
          <StyledContainerForFormView>
            {renderSocialMediaPostElement()}
          </StyledContainerForFormView>
        break;

      case enumDigitalMediaDisplayEnvironment.ListView:
        socialMediaPostContainerAndSocialMediaPostDisplay =
          <StyledContainerForListView>
            {renderSocialMediaPostElement()}
          </StyledContainerForListView>
        break;

      default:
        throw new MdbError(`Unhandled display environoment (${displayEnvironment}) in SocialMediaPostDisplay`);
    }

    return socialMediaPostContainerAndSocialMediaPostDisplay;
  }


  return (
    <>
      {renderSocialMediaPostContainerAndSocialMediaPostDisplay()}
    </>
  )
}
